/** Gap kept between a popover and the edge of the viewport. */
const MARGIN = 8;

/**
 * Roughly one line of the editor, used to lift a flipped popover clear of the
 * caret. The caret's own height is not reported by the range we anchor from, so
 * this is deliberately an approximation -- being a few pixels out moves the
 * menu, being unflipped puts it off-screen entirely.
 */
const CARET_HEIGHT = 24;

/**
 * Place a caret-anchored popover so it stays on screen.
 *
 * These menus are `position: fixed` at the caret, and the caret goes wherever
 * the agent is typing. On a desktop that is nearly always somewhere with room
 * around it; on a phone the caret sits hard against the right edge as a line
 * fills, and the composer itself is at the bottom of the screen with the
 * on-screen keyboard under it -- so both axes need clamping or the menu renders
 * somewhere it cannot be reached or scrolled to.
 *
 * The width is returned rather than set by a class so the value used for the
 * arithmetic and the value used for the layout can never drift apart.
 */
export function anchorStyle(
  top: number,
  left: number,
  width: number,
  height: number,
): { top: number; left: number; width: number } {
  if (typeof window === "undefined") return { top, left, width };

  const clampedLeft = Math.max(
    MARGIN,
    Math.min(left, window.innerWidth - width - MARGIN),
  );

  // Flip above the caret when there is no room below it.
  const flip = top + height + MARGIN > window.innerHeight;
  const clampedTop = flip
    ? Math.max(MARGIN, top - height - CARET_HEIGHT)
    : top;

  return { top: clampedTop, left: clampedLeft, width };
}
